import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LogOut, Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { useStats } from "@/context/StatsContext";
import { Panel, Empty, ResBadge, RowSearch, useQueryFilter, matchesQuery } from "@/pages/shared";
import MeterField from "@/components/MeterField";
import { uid, withUid } from "@/lib/uid";
import InvoiceDoneDialog from "@/components/InvoiceDoneDialog";
import SettlementExtras from "@/components/SettlementExtras";
import OutstandingCard from "@/components/OutstandingCard";
import OverdueDepartures from "@/components/OverdueDepartures";
import ExtendStay from "@/components/ExtendStay";

const today = () => new Date().toISOString().slice(0, 10);

export default function Vertrekkend() {
  const { t } = useLang();
  const { reload } = useStats();
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([]);
  const [taxRate, setTaxRate] = useState(21);
  const [deposit, setDeposit] = useState(0);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useQueryFilter();

  const [params] = useSearchParams();
  const openedRef = React.useRef(null);
  const [pitches, setPitches] = useState([]);
  const [meterNow, setMeterNow] = useState("");
  const [done, setDone] = useState(null); // factuur die nog afgedrukt of gemaild moet worden
  const [extras, setExtras] = useState(null);
  const [incl, setIncl] = useState([]);
  const load = async () => {
    try {
      const [r, p] = await Promise.all([api.get("/reservations?filter=departing"), api.get("/pitches")]);
      setRows(r.data); setPitches(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = params.get("id");
    if (id) api.get(`/reservations/${id}`).then((r) => r.data.status === "checked_in" && openCheckout(r.data)).catch(() => {});
    // openCheckout bewust buiten de deps: enkel reageren op een nieuwe ?id=
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  // Vanuit de plattegrond: ?pitch=A12 opent de uitcheck van het lopende verblijf op die plaats
  useEffect(() => {
    const label = params.get("pitch");
    if (!label || openedRef.current === label) return;
    openedRef.current = label;
    api.get("/reservations?filter=checked_in").then(({ data }) => {
      const r = data.find((x) => x.pitch_label === label && x.status === "checked_in");
      if (r) openCheckout(r);
      else toast.error(t("no_active_stay"));
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCheckout = async (r, meter = "", ex = null, inclIds = null) => {
    const end = r.departure > today() ? today() : r.departure;
    let extra = ex;
    if (!extra) {
      try { extra = (await api.get(`/reservations/${r.id}/settlement-extras?until=${end}`)).data; }
      catch (e) { extra = { parallel: [], sales: [], open_invoices: [] }; console.warn("Extra afrekengegevens niet opgehaald", e?.message); }
    }
    const ids = inclIds || extra.parallel.filter((p) => p.same_end).map((p) => p.reservation_id);
    let base = [];
    try {
      const mq = meter === "" || meter == null ? "" : `&meter_now=${Number(meter)}`;
      const alsoQ = ids.length ? `&also=${ids.join(",")}` : "";
      const { data: q } = await api.get(`/tariffs/quote?reservation_id=${r.id}&until=${end}${mq}${alsoQ}`);
      base = q.lines.map((l) => ({ description: l.description, qty: l.qty, unit_price: l.unit_price, kind: l.kind }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    if (!base.length) toast.info(t("tariff_no_tariffs"));
    (extra.sales || []).forEach((s) => base.push({
      description: `${s.description}${s.number ? ` (${s.number})` : ""}`, qty: 1, unit_price: s.total, kind: "shop",
    }));
    try {
      // Verhuur tot de vertrekdatum, in de taal van de gast
      const { data: ch } = await api.get(`/rentals/charges?reservation_id=${r.id}&until=${r.departure}&lang=${r.language || "nl"}`);
      ch.lines.forEach((l) => base.push({ description: l.description, qty: l.qty, unit_price: l.unit_price, kind: "rental" }));
      setDeposit(ch.deposit_total || 0);
    } catch (e) { console.debug("Waarborg niet opgehaald", e?.message); setDeposit(0); }
    setExtras(extra); setIncl(ids);
    setItems(withUid(base));
    setTaxRate(21);
    setSel(r);
    if (meter === "") setMeterNow("");
  };

  const toggleIncl = async (id) => {
    const next = incl.includes(id) ? incl.filter((x) => x !== id) : [...incl, id];
    if (sel) await openCheckout(sel, meterNow, extras, next);
  };

  const visible = rows.filter((r) => matchesQuery(r, q));

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const subtotal = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(taxRate) || 0) / 100;

  const doCheckout = async (withInvoice) => {
    setBusy(true);
    try {
      const pitch = pitches.find((p) => p.label === sel.pitch_label);
      if (pitch && meterNow !== "") {
        await api.post(`/pitches/${pitch.id}/meter`, { value: Number(meterNow), kind: "departure", reservation_id: sel.id });
      }
      const body = withInvoice
        ? {
            items: items.filter((i) => i.description).map(({ _uid, ...i }) => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price) })),
            tax_rate: Number(taxRate), currency: "EUR",
            sale_ids: (extras?.sales || []).map((s) => s.id),
            close_reservation_ids: incl.filter((id) => !(extras?.parallel || []).some((p) => p.reservation_id === id && p.same_end)),
            until: sel.departure > today() ? today() : sel.departure,
          }
        : { items: [] };
      const { data } = await api.post(`/reservations/${sel.id}/checkout`, body);
      if (withInvoice) {
        await api.post("/rentals/mark-billed", { reservation_id: sel.id, until: sel.departure })
          .catch((e) => console.warn("Verhuur niet als afgerekend gemarkeerd", e?.message));
      }
      const mailTo = sel.booker?.email || "";
      setSel(null);
      await Promise.all([load(), reload()]);
      toast.success(data.invoice ? `${t("checked_out_ok")} · ${t("invoice")} ${data.invoice.number}` : t("checked_out_ok"));
      // een eindafrekening is pas afgewerkt zodra de factuur afgedrukt of gemaild is
      if (data.invoice) setDone({ inv: data.invoice, email: mailTo });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Panel title={`${t("nav_vertrekkend")} (${visible.length}${q ? `/${rows.length}` : ""})`} testid="departing-panel"
      action={<RowSearch value={q} onChange={setQ} placeholder={t("search_placeholder")} testid="departing-search" />}>
      <OverdueDepartures onCheckout={(r) => api.get(`/reservations/${r.id}`).then(({ data }) => openCheckout(data))}
        onExtended={() => Promise.all([load(), reload()])} refreshKey={rows.length} />
      {visible.length === 0 ? <Empty>{t("no_departures")}</Empty> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("guest")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("arrival")}</TableHead>
              <TableHead>{t("departure")}</TableHead>
              <TableHead>{t("persons")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} data-testid={`departing-row-${r.id}`} className={r.departure < today() ? "bg-amber-50/60" : ""}>
                <TableCell className="font-medium">{r.guest_name}</TableCell>
                <TableCell><span className="font-semibold text-primary">{r.pitch_label}</span></TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(r.arrival)}</TableCell>
                <TableCell className={r.departure < today() ? "text-amber-600 font-medium" : "text-muted-foreground"}>{fmtDate(r.departure)}</TableCell>
                <TableCell>{r.persons}</TableCell>
                <TableCell><ResBadge status={r.status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <ExtendStay res={r} onDone={() => Promise.all([load(), reload()])} />
                    <Button size="sm" onClick={() => openCheckout(r)} data-testid={`checkout-${r.id}`}>
                      <LogOut className="h-4 w-4 mr-1" />{t("checkout")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Receipt className="h-4 w-4" />{t("final_invoice")} — {sel?.guest_name} · {sel?.pitch_label}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{fmtDate(sel?.arrival)} → {fmtDate(sel?.departure)} · {sel?.persons} {t("persons").toLowerCase()}</p>
          <MeterField meterStart={sel?.meter_start} value={meterNow} onChange={setMeterNow}
            lastKnown={pitches.find((p) => p.label === sel?.pitch_label)?.meter_last}
            onRecalc={() => sel && openCheckout(sel, meterNow, extras, incl)} />

          <SettlementExtras extras={extras} incl={incl} onToggle={toggleIncl} />
          <OutstandingCard guestId={sel?.guest_id} />
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-1">
              <span>{t("description")}</span><span>{t("qty")}</span><span>{t("unit_price")}</span><span className="text-right">{t("total")}</span><span />
            </div>
            {items.map((it, i) => (
              <div key={it._uid} className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 items-center" data-testid={`invoice-line-${i}`}>
                <Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
                <Input type="number" min="0" step="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`line-qty-${i}`} />
                <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`line-price-${i}`} />
                <span className="text-right text-sm tabular-nums">{money((Number(it.qty) || 0) * (Number(it.unit_price) || 0))}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setItems(items.filter((_, idx) => idx !== i))} data-testid={`line-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems([...items, { _uid: uid(), description: "", qty: 1, unit_price: 0 }])} data-testid="add-line"><Plus className="h-4 w-4 mr-1" />{t("add_line")}</Button>
          </div>

          <div className="ml-auto w-64 space-y-1 text-sm border-t border-slate-100 pt-3">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("subtotal")}</span><span className="tabular-nums" data-testid="checkout-subtotal">{money(subtotal)}</span></div>
            <div className="flex justify-between items-center gap-2">
              <span className="text-muted-foreground flex items-center gap-1">{t("vat")} <Input type="number" className="h-7 w-16 text-xs" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="checkout-tax" />%</span>
              <span className="tabular-nums">{money(tax)}</span>
            </div>
            {deposit > 0 && (
              <div className="flex justify-between text-[12px] text-amber-700" data-testid="checkout-deposit">
                <span>{t("rental_deposit_note")}</span><span className="tabular-nums">{money(deposit)}</span>
              </div>
            )}
            <div className="flex justify-between font-display font-extrabold text-lg text-primary"><span>{t("total")}</span><span className="tabular-nums" data-testid="checkout-total">{money(subtotal + tax)}</span></div>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button variant="ghost" onClick={() => doCheckout(false)} disabled={busy} data-testid="checkout-no-invoice">{t("checkout_only")}</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSel(null)}>{t("cancel")}</Button>
              <Button onClick={() => doCheckout(true)} disabled={busy || items.length === 0} data-testid="checkout-confirm"><LogOut className="h-4 w-4 mr-1" />{t("checkout_and_invoice")}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Een eindafrekening is pas afgewerkt zodra de factuur afgedrukt of gemaild is */}
      <InvoiceDoneDialog
        open={!!done}
        number={done?.inv?.number}
        email={done?.email}
        onFinish={async ({ print, mail }) => {
          if (print) window.open(`/print-factuur?invoice=${done.inv.id}`, "_blank", "noopener");
          if (mail) {
            try {
              await api.post(`/invoices/${done.inv.id}/send`);
              toast.success(t("invoice_mailed"));
            } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
          }
          setDone(null);
        }}
      />
    </Panel>
  );
}
